import type { ValueOf } from "@pdfking/forms"
import {
  ChoiceField,
  CreateFormArray,
  CreateFormObject,
  PercentageRecordField,
  StringField,
} from "@pdfking/forms"
import type { FormatEntity } from "@pdfking/imposition/internal"
import { JobDestinationList, LoadFormat, PRINT_DESTINATION_ID } from "@pdfking/imposition/internal"
import { POINT_KEYS } from "@pdfking/math/constants"
import { Constant, Items, RecordCreate } from "@pdfking/tfunc"

import type { CustomConfig } from "#public"

// Array bounds mirror the python schema (base.py:331). `min: 0` because an
// imposition with no files at all is a legitimate state — the panel opens
// empty, and the last row can be deleted like any other; a floor of 1 left an
// undeletable blank row behind and made "empty" unreachable.
const FILE_KEY_BOUNDS = { min: 0, max: 16 }

// What a file row may print on: a coating (lamination) covers the whole sheet
// and takes no file, so it is never a choice.
const FileDestinations = RecordCreate(
  Items(JobDestinationList).filter(([, d]) => d.print_mode !== "coating")
)

// Uploaded source files. Mirrors Python `MainJobMixin.file_key`
// (presets/base.py:306-337). Its own form, not part of any preset's: which
// files a job receives says nothing about how the job is imposed, and the rows
// look the same for every imposition kind — so the imposition never sees them
// (they reach the plot through {@link FilesToDestinations}).
const FileKeyField = (format: FormatEntity) =>
  CreateFormArray(
    CreateFormObject({
      file_id: StringField({ optional: true }),
      file_range: StringField({ optional: true }),
      file_destination: ChoiceField(FileDestinations, {
        format,
        initial: Constant(PRINT_DESTINATION_ID),
      }),
      file_scale: PercentageRecordField({
        keys: POINT_KEYS,
        initial: Constant("100%"),
      }),
    }),
    FILE_KEY_BOUNDS
  )

export type FilesSchema = { file_key: ValueOf<ReturnType<typeof FileKeyField>> }
export type FileSchema = FilesSchema["file_key"][number]

/** Build the files form with localized destination labels (loads i18n first). */
export const FilesForm = async ({ language = "en" }: Partial<CustomConfig> = {}) => {
  const { format_entity: format } = await LoadFormat(language)

  return CreateFormObject({ file_key: FileKeyField(format) })
}
